import React from "react";
import DOMPurify from "dompurify";
import { SYMBOL_LIBRARY } from "../../constants/symbols";
import { w2p, izoom } from "../../utils/coords";

const sanitizeSVG = raw =>
  DOMPurify.sanitize(raw || "", { USE_PROFILES: { svg: true, svgFilters: true } });

/**
 * Renders all placed symbols on the sketch canvas.
 * Symbols are stored as { id, type, x, y, rotation, layerId, label }
 * in st.symbols.
 */
export default function SymbolLayer({ st, selSymbolId, zoom, onSelect, onDragStart, dragSymbolId, dragPosMm, mode }) {
  const s    = izoom(zoom);
  const syms = Object.values(st.symbols || {});
  if (!syms.length) return null;

  return (
    <g>
      {syms.map(sym => {
        const layer = (st.layers || {})[sym.layerId];

        const def    = SYMBOL_LIBRARY[sym.type];
        if (!def) return null;

        const isSel  = sym.id === selSymbolId;
        // Layer visibility check (layer.visible is undefined only on legacy saves — default to visible)
        if (layer && layer.visible === false) return null;
        const isDrag = sym.id === dragSymbolId;
        const pos    = isDrag && dragPosMm ? { x: dragPosMm.x, y: dragPosMm.y } : sym;
        const cx     = w2p(pos.x);
        const cy     = w2p(pos.y);
        const size   = w2p(def.size * (sym.scale || 1));  // symbol bounding box in SVG px, scaled
        const color  = layer?.color || "#facc15";

        return (
          <g key={sym.id}
            transform={`translate(${cx},${cy}) rotate(${sym.rotation || 0}) translate(${-size/2},${-size/2})`}
            style={{ cursor: dragSymbolId === sym.id ? "grabbing" : mode === "select" ? "grab" : "pointer" }}
            onClick={ev => { ev.stopPropagation(); onSelect(sym.id); }}
            onMouseDown={ev => { ev.stopPropagation(); ev.preventDefault(); if (onDragStart && mode === "select") onDragStart(sym.id); }}
          >
            {/* Selection ring */}
            {isSel && (
              <rect x={-size*0.1} y={-size*0.1} width={size*1.2} height={size*1.2}
                fill="none" stroke="#e8a838" strokeWidth={s*1.5}
                strokeDasharray={`${s*4} ${s*3}`} rx={s*2}/>
            )}

            {/* Symbol SVG at correct size, coloured by layer */}
            <svg
              width={size} height={size}
              viewBox="0 0 100 100"
              overflow="visible"
              style={{ color, pointerEvents: "none" }}
            >
              <g fill="none" stroke={color} strokeWidth="5">
                <g dangerouslySetInnerHTML={{ __html: sanitizeSVG(def.svgPath) }}/>
              </g>
            </svg>

            {/* Label */}
            {sym.label && (
              <text
                x={size / 2} y={size + s * 12}
                textAnchor="middle" fontSize={s * 10}
                fontFamily="'JetBrains Mono',monospace" fill={color}
                style={{ pointerEvents: "none" }}
              >
                {sym.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
